// ts-rest contract for Comply > CMP Reviews.
// Composes the CmpReviewItem + CmpFinding Object Type rows behind one typed
// read. Per ADR-0018 the rows the server knows the shape of are real
// `z.object` schemas (the precise InferObject projection).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const cmpAreaSchema = z.enum([
    'BestExecution',
    'Suitability',
    'PersonalAccountDealing',
    'GiftsAndEntertainment',
    'FinancialPromotions',
    'Outsourcing',
    'DataProtection',
    'ComplaintsHandling',
    'ClientMoneyAndAssets',
    'ConflictsOfInterest',
    'MarketAbuseSurveillance',
    'ConsumerDuty',
]);

// The CmpReviewItem Object Type row (InferObject), projected field-by-field.
// `findingsCount` and the due-state are read-layer derivations (nextDueAt vs
// now), not stored on the row — the page derives them client-side.
export const cmpReviewItemDtoSchema = z.object({
    id: z.string(),
    area: cmpAreaSchema,
    description: z.string(),
    frequency: z.enum(['Monthly', 'Quarterly', 'SemiAnnual', 'Annual']),
    sampleSize: z.number(),
    lastReviewedAt: z.string().nullable(),
    nextDueAt: z.string(),
    ragStatus: z.enum(['Red', 'Amber', 'Green']),
    reviewerActorId: z.string().nullable(),
    detail: z.string().nullable(),
});
export type CmpReviewItemDto = z.infer<typeof cmpReviewItemDtoSchema>;

// The CmpFinding Object Type row (InferObject), projected field-by-field.
export const cmpFindingDtoSchema = z.object({
    id: z.string(),
    area: cmpAreaSchema,
    severity: z.enum(['Material', 'NonMaterial', 'PotentialMaterial']),
    description: z.string(),
    recommendation: z.string(),
    findingDate: z.string(),
    ownerActorId: z.string(),
    status: z.enum(['Open', 'InProgress', 'Closed']),
    resolvedAt: z.string().nullable(),
    resolutionNote: z.string().nullable(),
});
export type CmpFindingDto = z.infer<typeof cmpFindingDtoSchema>;

export const cmpReviewsResponseSchema = z.object({
    reviewItems: z.array(cmpReviewItemDtoSchema),
    findings: z.array(cmpFindingDtoSchema),
});
export type CmpReviewsResponse = z.infer<typeof cmpReviewsResponseSchema>;

const errorBody = z.object({ error: z.string() });
const okBody = z.object({ ok: z.literal(true) });

// Mirrors the user-supplied fields of the AddCmpFinding Action input (ADR-0008
// write-side rule): the body supplies the reviewer's real decision. `area` and
// `severity` are the ontology enums; `ownerActorId` is derived server-side from
// the acting officer (the officer raising the finding owns its remediation).
export const addCmpFindingBodySchema = z.object({
    area: cmpAreaSchema,
    severity: z.enum(['Material', 'NonMaterial', 'PotentialMaterial']),
    description: z.string().min(1),
    recommendation: z.string().min(1),
    clientId: z.string().uuid().optional(),
});
export type AddCmpFindingBody = z.infer<typeof addCmpFindingBodySchema>;

export const complyCmpReviewsContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/cmp-reviews',
            responses: { 200: cmpReviewsResponseSchema, 403: errorBody },
            summary: 'Composed Comply > CMP Reviews payload',
        },
        markReviewed: {
            method: 'POST',
            path: '/comply/cmp-reviews/:reviewItemId/mark-reviewed',
            pathParams: z.object({ reviewItemId: z.string() }),
            // ragStatus is the reviewer's verdict, threaded onto the
            // MarkCmpReviewed Action input (ADR-0008 write-side rule). Required:
            // the MarkCmpReviewed Action forbids an auto-Green default — the
            // reviewer must choose Red / Amber / Green explicitly.
            body: z.object({ ragStatus: z.enum(['Red', 'Amber', 'Green']) }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Mark a CMP review item as reviewed',
        },
        addFinding: {
            method: 'POST',
            path: '/comply/cmp-reviews/findings',
            body: addCmpFindingBodySchema,
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Add a CMP finding',
        },
        closeFinding: {
            method: 'POST',
            path: '/comply/cmp-reviews/findings/:findingId/close',
            pathParams: z.object({ findingId: z.string() }),
            // resolutionNote is the closer's rationale, threaded onto the
            // CloseCmpFinding Action input (ADR-0008 write-side rule). Required
            // and non-empty: the Action itself demands a non-empty note, so the
            // contract supplies one rather than fabricating a server constant.
            body: z.object({ resolutionNote: z.string().min(1) }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Close a CMP finding',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
